<template>
  <section
    v-if="isVisible"
    :id
    class="paragraph-teaser-list relative"
    :class="{
      'mt-section-y':
        options.teaserStyle === 'grid' || options.teaserStyle === 'slider',
    }"
  >
    <BlokkliField
      v-if="
        (options.teaserStyle === 'hero' || options.teaserStyle === 'slider') &&
        parentType !== 'accordion'
      "
      v-slot="{ items }"
      name="field_paragraphs"
      :should-render-item
      :list="filterNullArray(paragraphs)"
      proxy-mode
    >
      <HeroSlider
        v-if="options.teaserStyle === 'hero'"
        :key="getKey(items)"
        :items="filterVisibleItems(items)"
      />
      <TeaserSlider
        v-else
        :id="uuid"
        :items="filterVisibleItems(items)"
        :is-editing="isEditing"
      />
    </BlokkliField>

    <div v-else class="container">
      <div
        class="teaser-grid"
        :class="{
          'is-TeaserFull': teaserColumns === 'full',
          'is-TeaserHalf': teaserColumns === 'half',
          'is-TeaserThird': teaserColumns === 'third',
          'is-TeaserQuarter': teaserColumns === 'quarter',
          'is-TeaserFifth': teaserColumns === 'fifth',
          'is-numbered': options.isNumbered && parentType !== 'accordion',
        }"
      >
        <BlokkliField
          name="field_paragraphs"
          :list="filterNullArray(paragraphs)"
          :should-render-item
          class="teaser-grid-inner"
          :class="{
            '!gap-0': isDivided,
          }"
        />
      </div>
    </div>
    <div v-if="options.showMap" class="container mt-size-4">
      <TeaserMap
        :nids="nids"
        :map-id="'map-' + uuid"
        :external-blocks="externalBlocks"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { ParagraphTeaserListFragment } from '#graphql-operations'
import type { FieldListItem } from '#blokkli/types'
import { notNullish } from '@vueuse/core'
import { getParagraphOfBundle, type TeaserColumns } from '~/helpers/paragraphs'
import type { FieldListItemTyped } from '#blokkli-build/generated-types'
import { filterNullArray } from '~/helpers'

const props = withDefaults(
  defineProps<{
    paragraphs?: ParagraphTeaserListFragment['paragraphs']
  }>(),
  {
    paragraphs: () => [],
  },
)

const isEditing = false

const { options, uuid, rootBlocks, parentType, index, siblings } =
  defineBlokkli({
    bundle: 'teaser_list',
    globalOptions: [
      'contextToggleId',
      'bkHiddenGlobally',
      'publicationChannels',
    ],
    options: {
      teaserStyle: {
        type: 'radios',
        label: 'Style',
        default: 'grid',
        options: {
          grid: 'Grid',
          slider: 'Slider',
          hero: 'Hero',
        },
      },
      teaserColumns: {
        type: 'radios',
        label: 'Columns',
        default: 'fifth',
        options: {
          fifth: '20%',
          quarter: '25%',
          third: '33%',
          half: '50%',
          full: 'Full',
        },
      },

      isDivided: {
        type: 'checkbox',
        label: 'Divided',
        default: false,
      },
      isNumbered: {
        type: 'checkbox',
        label: 'Numbered',
        default: false,
      },
      showMap: {
        type: 'checkbox',
        label: 'Show Map',
        default: false,
      },
    },
    editor: {
      disableEdit: true,
      determineVisibleOptions: function (ctx) {
        if (ctx.parentType === 'accordion') {
          return [
            'bkHiddenGlobally',
            'contextToggleId',
            'bkHiddenGlobally',
            'publicationChannels',
            'teaserColumns',
          ]
        }

        if (
          ctx.options.teaserStyle === 'slider' ||
          ctx.options.teaserStyle === 'hero'
        ) {
          return [
            'teaserStyle',
            'contextToggleId',
            'bkHiddenGlobally',
            'publicationChannels',
          ]
        }

        if (ctx.options.teaserColumns === 'full') {
          return [
            'teaserColumns',
            'isNumbered',
            'isDivided',
            'showMap',
            'teaserStyle',
            'contextToggleId',
            'bkHiddenGlobally',
            'publicationChannels',
          ]
        }

        return [
          'teaserColumns',
          'showMap',
          'teaserStyle',
          'contextToggleId',
          'bkHiddenGlobally',
          'publicationChannels',
        ]
      },
    },
    propsFieldMapping: {
      paragraphs: { type: 'field', name: 'field_paragraphs' },
    },
  })

const id = useParagraphAnchorId(index, siblings)

const { determineVisibility } = usePartyToggle()
const { shouldRenderItem } = useParagraphVisiblity()
const isVisible = determineVisibility(options, rootBlocks)

const teaserColumns = computed<TeaserColumns>(() => {
  if (
    parentType.value !== 'accordion' &&
    options.value.teaserStyle === 'slider'
  ) {
    return 'third'
  }

  return options.value.teaserColumns
})

function filterVisibleItems(items: FieldListItemTyped[]) {
  if (isEditing) {
    return items.filter((v) => shouldRenderItem(v))
  }

  return items
}

const isDivided = computed(
  () =>
    options.value.isDivided &&
    parentType.value !== 'accordion' &&
    teaserColumns.value === 'full',
)

const isNumbered = computed(
  () =>
    options.value.isNumbered &&
    parentType.value !== 'accordion' &&
    teaserColumns.value === 'full',
)

provideParagraphContext('teaserColumns', teaserColumns)
provideParagraphContext('teaserDivided', isDivided)
provideParagraphContext('teaserNumbered', isNumbered)

function getKey(items: FieldListItem[]) {
  if (isEditing) {
    // During editing, we have to force a rerender of the slider when the count
    // or the order of paragraphs changes.
    return items.map((v) => v.uuid).join('-')
  }

  return 'slider'
}

const nids = computed(() =>
  props.paragraphs
    .map((v) => {
      const teaser = getParagraphOfBundle(v, 'teaser')
      if (teaser && teaser.link?.uri) {
        const uri = teaser.link.uri
        if (
          uri &&
          'entity' in uri &&
          uri.entity &&
          'id' in uri.entity &&
          uri.entity.id
        ) {
          return uri.entity.id
        }
      }

      return null
    })
    .filter(notNullish),
)

const externalBlocks = computed(() => {
  return props.paragraphs
    .map((v) => {
      if (!v?.props) {
        return null
      }
      if (!('link' in v.props)) {
        return null
      }

      const uri = v.props.link?.uri

      if (!uri) {
        return null
      }

      return v.props
    })
    .filter(notNullish)
})
</script>

<style lang="postcss">
.paragraph-title + .paragraph-teaser-list {
  @apply !mt-0;
}
.paragraph-text + .paragraph-teaser-list {
  @apply !mt-0;
}

/* When nested in a toggle container */
.paragraph-teaser-list {
  .toggle-container & {
    .group\/slider {
      @apply px-0;
    }
    .teaser-slider {
      @apply belowTablet:px-0;
    }
  }
}
</style>
